import React, { useEffect, useState, useRef } from 'react';
import { getTenant } from '../../../theme/tokens';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useCompany } from '../../../app/providers/CompanyProvider';
import { ShoppingBagIcon, StoreIcon, GlobeIcon, PlusIcon, EyeIcon, EyeOffIcon, PackageIcon, DollarSignIcon, MoreHorizontalIcon, CreditCardIcon, TrashIcon, EditIcon, Share2Icon, SearchIcon, LinkIcon, CopyIcon, QrCodeIcon, ChevronRightIcon, XIcon, CheckCircleIcon, XCircleIcon } from 'lucide-react';
// Shop type definition
export interface Shop {
  id: string;
  name: string;
  description: string;
  logo: string;
  color: string;
  isOpen: boolean;
  channels: ('pos' | 'ecommerce')[];
  productsCount: number;
  revenue: {
    total: number;
    currency: string;
    growth: number;
  };
  ordersCount: number;
  companyId: string;
}
export function Shops() {
  const {
    t
  } = useTranslation('shops');
  const navigate = useNavigate();
  const {
    selectedCompany
  } = useCompany();
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState<'all' | 'open' | 'closed'>('all');
  const [contextMenuOpenId, setContextMenuOpenId] = useState<string | null>(null);
  const [shareModalShopId, setShareModalShopId] = useState<string | null>(null);
  const [showQRCode, setShowQRCode] = useState(false);
  const contextMenuRef = useRef<HTMLDivElement>(null);
  const [shops, setShops] = useState<Shop[]>([{
    id: '1',
    name: 'Beach Shop',
    description: 'Beachwear and accessories for summer holidays',
    logo: '•',
    color: '#f8d7d7',
    isOpen: true,
    channels: ['pos', 'ecommerce'],
    productsCount: 48,
    revenue: {
      total: 12580,
      currency: getTenant().currency.code,
      growth: 15.3
    },
    ordersCount: 124,
    companyId: 'submerchant-kl-001'
  }, {
    id: '2',
    name: 'Island Shop',
    description: 'Local island products and souvenirs',
    logo: '+',
    color: '#e1f5fe',
    isOpen: true,
    channels: ['ecommerce'],
    productsCount: 32,
    revenue: {
      total: 8750,
      currency: getTenant().currency.code,
      growth: 8.7
    },
    ordersCount: 86,
    companyId: 'submerchant-kl-002'
  }, {
    id: '3',
    name: 'ABC',
    description: 'Convenience store with everyday essentials',
    logo: 'A',
    color: '#e8f5e9',
    isOpen: false,
    channels: ['pos'],
    productsCount: 156,
    revenue: {
      total: 24680,
      currency: getTenant().currency.code,
      growth: -2.4
    },
    ordersCount: 312,
    companyId: 'submerchant-kl-003'
  }]);
  // Filter shops by selected company
  const companyFilteredShops = selectedCompany.type === 'master' ? shops : shops.filter(s => s.companyId === selectedCompany.id);
  // Close context menu on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (contextMenuRef.current && !contextMenuRef.current.contains(event.target as Node)) {
        setContextMenuOpenId(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const toggleShopStatus = (shopId: string) => {
    setShops(shops.map(shop => shop.id === shopId ? {
      ...shop,
      isOpen: !shop.isOpen
    } : shop));
    setContextMenuOpenId(null);
  };
  const deleteShop = (shopId: string) => {
    if (confirm('Are you sure you want to delete this shop? This action cannot be undone.')) {
      setShops(shops.filter(shop => shop.id !== shopId));
    }
    setContextMenuOpenId(null);
  };
  const copyShopUrl = (shopId: string) => {
    const shopUrl = `https://yourwebsite.com/shop/${shopId}`;
    navigator.clipboard.writeText(shopUrl);
  };
  const filteredShops = companyFilteredShops.filter(shop => {
    const matchesSearch = searchQuery === '' || shop.name.toLowerCase().includes(searchQuery.toLowerCase()) || shop.description.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesTab = activeTab === 'all' || activeTab === 'open' && shop.isOpen || activeTab === 'closed' && !shop.isOpen;
    return matchesSearch && matchesTab;
  });
  const totalRevenue = companyFilteredShops.reduce((sum, s) => sum + s.revenue.total, 0);
  const totalProducts = companyFilteredShops.reduce((sum, s) => sum + s.productsCount, 0);
  const totalOrders = companyFilteredShops.reduce((sum, s) => sum + s.ordersCount, 0);
  const getStatusBadge = (isOpen: boolean) => {
    return isOpen ? <span className="inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium bg-green-100 text-green-800" data-id="element-13073" data-mp="3nm1osxbl0nmc">
        {t('components.getOrderStatusBadge.open')}
      </span> : <span className="inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium bg-red-100 text-red-800" data-id="element-13074" data-mp="3b3za3yxy0487">
        {t('pages.getStatusBadge.closed')}
      </span>;
  };
  return <div className="max-w-[1400px] mx-auto space-y-8" data-id="element-13075" data-mp="3k4dw70iqdncr">
      {/* Header */}
      <div className="flex justify-between items-center" data-id="element-13076" data-mp="3k2awtpvouhvd">
        <h1 className="text-2xl font-semibold text-gray-900" data-id="element-13077" data-mp="2wfunwkzolako">
          {t('pages.Shops.shops')}
        </h1>
        <div className="flex items-center gap-3" data-id="element-13078" data-mp="2rh18gn0u39hv">
          <button onClick={() => navigate('/shop-details?new=true')} className="px-4 py-2 bg-[#E2231A] hover:bg-[#C41B14] text-white text-sm font-medium rounded-lg transition-colors shadow-sm" data-id="element-13079" data-mp="364mfut4qruvl">
            {t('pages.Shops.add_shop')}
          </button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-id="element-13080" data-mp="28o77wy3yjjx6">
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden flex flex-col" data-id="element-13081" data-mp="cmc4tfxwexgl">
          <div className="px-4 py-2.5 border-b border-gray-100 flex items-center gap-2" data-id="element-13082" data-mp="hkx6ljsstp75">
            <StoreIcon size={16} className="text-gray-500" data-id="element-13083" data-mp="mbvnollm2rbg" />
            <span className="text-sm font-semibold text-gray-900" data-id="element-13084" data-mp="mcf603f6bhq0">
              {t('pages.Shops.total_shops')}
            </span>
          </div>
          <div className="px-4 py-4 flex-1" data-id="element-13085" data-mp="3psvpx5su07ra">
            <div className="text-2xl font-bold text-gray-900" data-id="element-13086" data-mp="3ptxbi47h4cn9">
              {shops.length}
            </div>
          </div>
          <div className="px-4 py-2 bg-gray-50 text-xs text-gray-600 font-medium" data-id="element-13087" data-mp="3utt8hjhr73tb">
            {shops.filter(s => s.isOpen).length} {t('pages.Shops.open')}{' '}
            {shops.filter(s => !s.isOpen).length} {t('pages.Shops.closed')}
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden flex flex-col" data-id="element-13088" data-mp="1e29xa5mldhhb">
          <div className="px-4 py-2.5 border-b border-gray-100 flex items-center gap-2" data-id="element-13089" data-mp="1j0uvy0m73t3n">
            <DollarSignIcon size={16} className="text-gray-500" data-id="element-13090" data-mp="1t51o5d23rf4x" />
            <span className="text-sm font-semibold text-gray-900" data-id="element-13091" data-mp="1o79vggtmvozo">
              {t('pages.Shops.total_revenue')}
            </span>
          </div>
          <div className="px-4 py-4 flex-1" data-id="element-13092" data-mp="1jeqinm2rsxvx">
            <div className="text-2xl font-bold text-gray-900" data-id="element-13093" data-mp="1fey4mtulxzj6">
              {t('components.ShopDashboard.usd_2')}{' '}
              {totalRevenue.toLocaleString('en-US')}
            </div>
          </div>
          <div className="px-4 py-2 bg-gray-50 text-xs text-gray-600 font-medium" data-id="element-13094" data-mp="15gs7epjqxhkn">
            {t('pages.Orders.across')} {shops.length} {t('pages.Shops.shops_2')}
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden flex flex-col" data-id="element-13095" data-mp="wg3obgfhwr0w">
          <div className="px-4 py-2.5 border-b border-gray-100 flex items-center gap-2" data-id="element-13096" data-mp="2uv6n6kcsdu40">
            <PackageIcon size={16} className="text-gray-500" data-id="element-13097" data-mp="2qtv6x5fsyl8h" />
            <span className="text-sm font-semibold text-gray-900" data-id="element-13098" data-mp="2qud4c5e7b5hz">
              {t('pages.Shops.total_products')}
            </span>
          </div>
          <div className="px-4 py-4 flex-1" data-id="element-13099" data-mp="3e3yf9grnhvgv">
            <div className="text-2xl font-bold text-gray-900" data-id="element-13100" data-mp="3e3oss35te6fx">
              {totalProducts}
            </div>
          </div>
          <div className="px-4 py-2 bg-gray-50 text-xs text-gray-600 font-medium" data-id="element-13101" data-mp="30az2hmj96fd0">
            {t('pages.Shops.all_shops_combined')}
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden flex flex-col" data-id="element-13102" data-mp="2qdugpntbklwq">
          <div className="px-4 py-2.5 border-b border-gray-100 flex items-center gap-2" data-id="element-13103" data-mp="2vei6y79q4ypw">
            <ShoppingBagIcon size={16} className="text-gray-500" data-id="element-13104" data-mp="2hez5tyfbynn3" />
            <span className="text-sm font-semibold text-gray-900" data-id="element-13105" data-mp="2mcqxcrc2c7qr">
              {t('pages.Orders.total_orders')}
            </span>
          </div>
          <div className="px-4 py-4 flex-1" data-id="element-13106" data-mp="fn3z7x7xl9bq">
            <div className="text-2xl font-bold text-gray-900" data-id="element-13107" data-mp="kldx4fl1io11">
              {totalOrders}
            </div>
          </div>
          <div className="px-4 py-2 bg-gray-50 text-xs text-gray-600 font-medium" data-id="element-13108" data-mp="uosvomqwumeo">
            {t('pages.Shops.all_shops_combined')}
          </div>
        </div>
      </div>

      {/* Main Content Card */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm" data-id="element-13109" data-mp="13ixuptg7745a">
        {/* Controls Bar */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between p-4 border-b border-gray-200 gap-4" data-id="element-13110" data-mp="ypmjzh89m383">
          {/* Tabs */}
          <div className="flex space-x-6" role="tablist" aria-label="Shop status tabs" data-id="element-13111" data-mp="18h95c6ebhiok">
            {[{
            id: 'all',
            label: 'All'
          }, {
            id: 'open',
            label: 'Open'
          }, {
            id: 'closed',
            label: 'Closed'
          }].map(tab => <button key={tab.id} role="tab" aria-selected={activeTab === tab.id} aria-controls="shops-tabpanel" id={`shops-tab-${tab.id}`} onClick={() => setActiveTab(tab.id as 'all' | 'open' | 'closed')} className={`pb-4 -mb-4 text-sm font-medium transition-colors border-b-2 ${activeTab === tab.id ? 'border-[#E2231A] text-[#9F140F]' : 'border-transparent text-gray-500 hover:text-gray-700'}`} data-id="element-13112" data-mp="3k8crhvfu492r">
                {tab.label}
              </button>)}
          </div>

          {/* Search */}
          <div className="relative w-full sm:w-64" data-id="element-13113" data-mp="32uomi41r4x2x">
            <SearchIcon size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" data-id="element-13114" data-mp="32tdj45c7xrcc" />

            <input type="search" aria-label={t('pages.Shops.attrs.search_shops')} placeholder={t('pages.Shops.attrs.search_shops')} value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A] transition-colors" data-id="element-13115" data-mp="3vcsvwkhoyjyf" />
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto" data-id="element-13116" data-mp="gpn547yl8t7h">
          <table className="w-full text-left border-collapse" data-id="element-13117" data-mp="kq729bj5vwtm">
            <thead data-id="element-13118" data-mp="knuiqtxr9eeg">
              <tr className="border-b border-gray-100" data-id="element-13119" data-mp="pp0dsyew1dr5">
                <th className="px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-13120" data-mp="pnfl7ec4w4vx">
                  {t('pages.Orders.shop')}
                </th>
                <th className="px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-13121" data-mp="zf358u6d2jnd">
                  {t('pages.Shops.channels')}
                </th>
                <th className="px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-13122" data-mp="18fhrpwv2a4sg">
                  {t('components.CampaignList.status')}
                </th>
                <th className="px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-13123" data-mp="35e1g8d5lxtoo">
                  {t('pages.ShopDetails.products')}
                </th>
                <th className="px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-13124" data-mp="3e993tl4qw1s1">
                  {t('components.ShopDashboard.revenue')}
                </th>
                <th className="px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-13125" data-mp="3o5u7a21494tr">
                  {t('pages.Orders.orders')}
                </th>
                <th className="px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider text-right" data-id="element-13126" data-mp="11xp2pzrz3tp">
                  {t('pages.CampaignDetails.actions')}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100" data-id="element-13127" data-mp="22siuht50ytu7">
              {filteredShops.map(shop => <tr key={shop.id} onClick={() => navigate(`/shop-details?id=${shop.id}`)} className="hover:bg-gray-50 cursor-pointer transition-colors group" data-id="element-13128" data-mp="1ysqtc1hynpyv">
                  <td className="px-6 py-4" data-id="element-13129" data-mp="1sokioikg8v8v">
                    <div className="flex items-center gap-3" data-id="element-13130" data-mp="1eyh32ag2rzse">
                      <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{
                    backgroundColor: shop.color
                  }} data-id="element-13131" data-mp="1evuuq8ld1g94">
                        <span className="text-base font-bold text-red-600" data-id="element-13132" data-mp="19znn8bdbfwp2">
                          {shop.logo}
                        </span>
                      </div>
                      <div data-id="element-13133" data-mp="10xo8jpprx4pa">
                        <div className="text-sm font-medium text-gray-900 group-hover:text-[#16CD88] transition-colors" data-id="element-13134" data-mp="10yzcpqn4segx">
                          {shop.name}
                        </div>
                        <div className="text-sm text-gray-500 mt-0.5" data-id="element-13135" data-mp="2vb5td4ysnngm">
                          {shop.description}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4" data-id="element-13136" data-mp="qbo1ziyjbkxh">
                    <div className="flex items-center gap-1.5" data-id="element-13137" data-mp="lbi6tst23gan">
                      {shop.channels.includes('pos') && <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium bg-purple-50 text-purple-700 border border-purple-100" data-id="element-13138" data-mp="h951v6m4vmex">
                          <CreditCardIcon size={11} className="mr-1" data-id="element-13139" data-mp="7kn5d1kw01h3" />
                          {t('pages.ShopDetails.pos')}
                        </span>}
                      {shop.channels.includes('ecommerce') && <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium bg-blue-50 text-blue-700 border border-blue-100" data-id="element-13140" data-mp="3tswf5pibhwlv">
                          <GlobeIcon size={11} className="mr-1" data-id="element-13141" data-mp="3kr5ex06jqhze" />
                          {t('pages.ShopDetails.ecommerce')}
                        </span>}
                    </div>
                  </td>
                  <td className="px-6 py-4" data-id="element-13142" data-mp="256we1qankn84">{getStatusBadge(shop.isOpen)}</td>
                  <td className="px-6 py-4" data-id="element-13143" data-mp="29915qjhjj9s4">
                    <div className="text-sm font-medium text-gray-900" data-id="element-13144" data-mp="29ac76cnik5g2">
                      {shop.productsCount}
                    </div>
                  </td>
                  <td className="px-6 py-4" data-id="element-13145" data-mp="1hj63xnchxrmk">
                    <div className="text-sm font-medium text-gray-900" data-id="element-13146" data-mp="1hga57wstqyx2">
                      {t('components.ShopDashboard.usd_2')}{' '}
                      {shop.revenue.total.toLocaleString('en-US')}
                    </div>
                    <div className={`text-xs mt-0.5 ${shop.revenue.growth >= 0 ? 'text-green-600' : 'text-red-600'}`} data-id="element-13147" data-mp="1v9hxlqoqbyft">
                      {shop.revenue.growth >= 0 ? '+' : ''}
                      {shop.revenue.growth}%
                    </div>
                  </td>
                  <td className="px-6 py-4" data-id="element-13148" data-mp="263uvc8cqsx2y">
                    <div className="text-sm font-medium text-gray-900" data-id="element-13149" data-mp="262jrk8jarpbu">
                      {shop.ordersCount}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-right relative" data-id="element-13150" data-mp="2ewy5a6o6irsu">
                    <div ref={contextMenuOpenId === shop.id ? contextMenuRef : null} data-id="element-13151" data-mp="2av318uev60bd">
                      <button className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md transition-colors" onClick={e => {
                    e.stopPropagation();
                    setContextMenuOpenId(contextMenuOpenId === shop.id ? null : shop.id);
                  }} aria-label="Shop actions" data-id="element-13152" data-mp="1n7mler8sby1b">
                        <MoreHorizontalIcon size={18} aria-hidden="true" data-id="element-13153" data-mp="32od8s2rozf5o" />
                      </button>

                      {contextMenuOpenId === shop.id && <>
                          <div className="fixed inset-0 z-10" onClick={e => {
                      e.stopPropagation();
                      setContextMenuOpenId(null);
                    }} data-id="element-13154" data-mp="3f67kq22xa9mc" />

                          <div className="absolute right-4 top-full mt-1 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-20" data-id="element-13155" data-mp="3tvvm13b3t8a2">
                            <button onClick={e => {
                        e.stopPropagation();
                        setContextMenuOpenId(null);
                        navigate(`/shop-details?id=${shop.id}`);
                      }} className="w-full flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50" data-id="element-13156" data-mp="3k4hhui87jkal">
                              <EyeIcon size={16} className="mr-3 text-gray-400" data-id="element-13157" data-mp="2hiey8keznz8j" />

                              {t('pages.Orders.view_details')}
                            </button>
                            <button onClick={e => {
                        e.stopPropagation();
                        setContextMenuOpenId(null);
                        navigate(`/shop-details?id=${shop.id}&edit=true`);
                      }} className="w-full flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50" data-id="element-13158" data-mp="23r0f8dl2zy1h">
                              <EditIcon size={16} className="mr-3 text-gray-400" data-id="element-13159" data-mp="1g1fw9uoxh0s7" />

                              {t('pages.ShopDetails.attrs.edit_shop')}
                            </button>
                            <button onClick={e => {
                        e.stopPropagation();
                        setShareModalShopId(shop.id);
                        setShowQRCode(false);
                        setContextMenuOpenId(null);
                      }} className="w-full flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50" data-id="element-13160" data-mp="3rnzthfsj6p18">
                              <Share2Icon size={16} className="mr-3 text-gray-400" data-id="element-13161" data-mp="j8zosgc0mt96" />

                              {t('pages.Shops.share_shop')}
                            </button>
                            <button onClick={e => {
                        e.stopPropagation();
                        toggleShopStatus(shop.id);
                      }} className="w-full flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50" data-id="element-13162" data-mp="2ozuzsv15b7o0">
                              {shop.isOpen ? <>
                                  <EyeOffIcon size={16} className="mr-3 text-gray-400" data-id="element-13163" data-mp="2nklz99cd4eff" />

                                  {t('pages.Shops.close_shop')}
                                </> : <>
                                  <EyeIcon size={16} className="mr-3 text-gray-400" data-id="element-13164" data-mp="36gbzos4nd2r7" />

                                  {t('pages.Shops.open_shop')}
                                </>}
                            </button>
                            <div className="border-t border-gray-100 my-1" data-id="element-13165" data-mp="zto6h69qr5xq" />
                            <button onClick={e => {
                        e.stopPropagation();
                        deleteShop(shop.id);
                      }} className="w-full flex items-center px-4 py-2 text-sm text-red-600 hover:bg-red-50" data-id="element-13166" data-mp="zrjrntab0mio">
                              <TrashIcon size={16} className="mr-3" data-id="element-13167" data-mp="1ikfdi169ak8y" />
                              {t('pages.Shops.delete_shop')}
                            </button>
                          </div>
                        </>}
                    </div>
                  </td>
                </tr>)}
              {filteredShops.length === 0 && <tr data-id="element-13168" data-mp="3tavz5njx4vgc">
                  <td colSpan={7} className="px-6 py-12 text-center text-gray-500 text-sm" data-id="element-13169" data-mp="3tadwo96nxkzz">
                    {t('pages.Shops.no_shops_found_matching_your_criteria')}
                  </td>
                </tr>}
            </tbody>
          </table>
        </div>
      </div>

      {/* Share Modal */}
      {shareModalShopId && (() => {
      const shop = shops.find(s => s.id === shareModalShopId);
      if (!shop) return null;
      return <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" data-id="element-13170" data-mp="26f7nt03smc72">
              <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6 border border-gray-200" data-id="element-13171" data-mp="1snt3mpy5g4yf">
                <div className="flex justify-between items-center mb-5" data-id="element-13172" data-mp="1xmntucyg4vsk">
                  <h3 className="text-lg font-semibold text-gray-900" data-id="element-13173" data-mp="1xkta31ywv9q2">
                    {t('pages.Shops.share')} {shop.name}
                  </h3>
                  <button onClick={() => setShareModalShopId(null)} aria-label="Close" className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md transition-colors" data-id="element-13174" data-mp="1nozq4xiv4dy9">
                    <XIcon size={18} aria-hidden="true" data-id="element-13175" data-mp="1e144ip7t7tsk" />
                  </button>
                </div>

                {showQRCode ? <div className="text-center" data-id="element-13176" data-mp="190zot1eq733e">
                    <div className="bg-white p-4 rounded-lg inline-block mb-4 border border-gray-200" data-id="element-13177" data-mp="194dm3v8j8m3z">
                      <div className="w-48 h-48 bg-gray-50 rounded flex items-center justify-center" data-id="element-13178" data-mp="1j06zfgu2sjmt">
                        <QrCodeIcon size={80} className="text-gray-300" data-id="element-13179" data-mp="1wrlred5ynv60" />
                      </div>
                    </div>
                    <p className="text-sm text-gray-600 mb-4" data-id="element-13180" data-mp="1rs8xj8go9ntx">
                      {t('pages.Shops.scan_this_qr_code_to_visit')} {shop.name}
                    </p>
                    <button onClick={() => setShowQRCode(false)} className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-gray-700 text-sm font-medium transition-colors" data-id="element-13181" data-mp="21qop1cjm2z3y">
                      {t('pages.Shops.back_to_share_options')}
                    </button>
                  </div> : <>
                    <div className="space-y-3 mb-5" data-id="element-13182" data-mp="dsxl1g58x4lu">
                      <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-100" data-id="element-13183" data-mp="dse70aipv0d3">
                        <div className="flex items-center" data-id="element-13184" data-mp="3ccnpnxexb32r">
                          <LinkIcon size={16} className="text-gray-400 mr-2" data-id="element-13185" data-mp="3hafjiwz4oz9l" />
                          <span className="text-sm text-gray-600 truncate max-w-[220px]" data-id="element-13186" data-mp="3m8hdicy3eizu">
                            https://yourwebsite.com/shop/{shareModalShopId}
                          </span>
                        </div>
                        <button onClick={() => copyShopUrl(shareModalShopId)} aria-label="Copy shop URL" className="p-1.5 text-[#9F140F] hover:bg-[#E2231A]/10 rounded-md transition-colors" data-id="element-13187" data-mp="11i03gq7pf1he">
                          <CopyIcon size={16} aria-hidden="true" data-id="element-13188" data-mp="2m1veu1s1jyds" />
                        </button>
                      </div>

                      <button onClick={() => setShowQRCode(true)} className="w-full flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-100 hover:bg-gray-100 transition-colors" data-id="element-13189" data-mp="2h7r91luihwea">
                        <div className="flex items-center" data-id="element-13190" data-mp="238hsum6b32ab">
                          <QrCodeIcon size={16} className="text-gray-400 mr-2" data-id="element-13191" data-mp="1yag8zz122yxs" />

                          <span className="text-sm text-gray-600" data-id="element-13192" data-mp="1tdzjp1i4eej4">
                            {t('pages.Shops.generate_qr_code')}
                          </span>
                        </div>
                        <ChevronRightIcon size={16} className="text-gray-400" data-id="element-13193" data-mp="3nnti01k45b58" />
                      </button>
                    </div>

                    <div className="flex justify-end" data-id="element-13194" data-mp="1qr73whzl1mej">
                      <button onClick={() => setShareModalShopId(null)} className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-gray-700 text-sm font-medium transition-colors" data-id="element-13195" data-mp="1qsrw40yfct98">
                        {t('pages.Shops.close')}
                      </button>
                    </div>
                  </>}
              </div>
            </div>;
    })()}
    </div>;
}